import { useState } from 'react';

import omit from 'lodash/omit';
import pick from 'lodash/pick';
import PropTypes from 'prop-types';
import shortid from 'shortid';

import Autocomplete from '@mui/material/Autocomplete';

import { useTransl } from 'shared/lib/i18n';

import { sortByKey } from '../../utils';

import StyledTextField from '../Shared/StyledTextField';

const selectedOption = [
  '& .MuiAutocomplete-option[aria-selected="true"]',
  '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused',
].join(', ');
const focusedOption = '& .MuiAutocomplete-option.Mui-focused';
const fieldProps = ['placeholder', 'error'];

const StyledSelect = (props) => {
  const { sortAlphabetically, limitTags, options, ...rest } = props;

  const [inputValue, setInputValue] = useState('');
  const { t } = useTransl();

  const textFieldProps = pick(rest, fieldProps);
  const selectProps = omit(rest, fieldProps);

  const sortedOptions = sortAlphabetically
    ? options.sort((a, b) => sortByKey(a, b, 'label'))
    : options;

  const onInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const getIsOptionEqualToValue = (option, value) =>
    // eslint-disable-next-line eqeqeq
    option?.value == value?.value;

  return (
    <Autocomplete
      inputValue={inputValue}
      onInputChange={onInputChange}
      {...selectProps}
      options={sortedOptions}
      getOptionLabel={(option) => option?.label || ''}
      getOptionDisabled={(option) => option?.isDisabled}
      isOptionEqualToValue={getIsOptionEqualToValue}
      renderOption={(props, option) => (
        <li {...props} key={option?.label && shortid.generate()}>
          {option?.label}
        </li>
      )}
      size="small"
      limitTags={limitTags}
      renderInput={(params) => (
        <StyledTextField
          placeholder={t('actions.select')}
          {...params}
          {...textFieldProps}
        />
      )}
      sx={{ minWidth: '15rem' }}
      ListboxProps={{
        sx: {
          padding: 0,
          [selectedOption]: {
            color: '#fff',
            backgroundColor: (theme) => theme.palette.primary.main,
          },
          [focusedOption]: {
            color: 'inherit',
            backgroundColor: 'rgb(222, 235, 255)',
          },
        },
      }}
    />
  );
};

StyledSelect.defaultProps = {
  sortAlphabetically: true,
  limitTags: 5,
};

StyledSelect.propTypes = {
  options: PropTypes.instanceOf(Array).isRequired,
  sortAlphabetically: PropTypes.bool,
  limitTags: PropTypes.number,
};

export default StyledSelect;
